import React, { useEffect, useState, useMemo } from "react";
import { Table, InputGroup, FormControl, Spinner } from "react-bootstrap";
import { useTable, useFilters, useGlobalFilter, useSortBy, useResizeColumns, useFlexLayout } from "react-table";
import styled from 'styled-components';
import tw from "twin.macro";
import axios from 'axios';

const Container = tw.div`container bg-white rounded border border-gray-200 shadow-lg p-4`;
const Title = tw.h1`text-xl font-bold text-blue-600 mb-2 text-center`;
const TableWrapper = tw.div`mt-4 overflow-x-auto`;
const ErrorMessage = styled.p`
  ${tw`text-red-600 font-semibold mt-1 mb-3`}
`;

const StyledTable = styled(Table)`
  ${tw`min-w-full`}
  font-size: 12px;
  font-family: 'Arial', sans-serif;
  border: 1px solid #ccc;
  border-collapse: collapse;
  width: 100%;

  thead {
    ${tw`bg-gray-200`}
    
    th {
      ${tw`border border-gray-300 text-center`}
      background-color: #f0f0f0;
      font-weight: bold;
      white-space: normal;
      text-overflow: ellipsis;
      overflow: hidden;
      padding: 8px;
      position: relative;
      white-space: pre-wrap; /* Allows wrapping of long words */
      word-wrap: break-word; /* Wraps words to the next line */
    }
  }

  tbody {
    tr {
      ${tw`border-b border-gray-200`}

      &:hover {
        ${tw`bg-gray-100`}
      }
    }

    td {
      ${tw`border border-gray-300 py-2 px-4`}
      padding: 8px;
      white-space: pre-wrap; /* Allows wrapping of long words */
      word-wrap: break-word; /* Wraps words to the next line */
    }

    .align-right {
      text-align: right;
    }

    .align-left {
      text-align: left;
    }
  }
`;

const FilterInput = styled(FormControl)`
  ${tw`border border-gray-300`}
  height: 30px;
  font-size: 14px;
`;

const ResizableHeader = ({ className, column }) => {
  const { getHeaderProps, getSortByToggleProps, isSorted, isSortedDesc, canResize } = column;

  return (
    <th className={className} {...getHeaderProps()}>
      <div style={{ position: 'relative', width: '100%' }}>
        <span>{column.render('Header')}</span>
        {column.canSort && (
          <button
            className="sort-button"
            {...getSortByToggleProps()}
            style={{ padding: '0', marginLeft: '5px', fontSize: '12px', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {isSorted ? (isSortedDesc ? '↓' : '↑') : '↕'}
          </button>
        )}
        {canResize && (
          <div
            {...column.getResizerProps()}
            className="resizer"
            style={{ right: 0, position: 'absolute', top: 0, bottom: 0, cursor: 'col-resize', width: '10px' }}
          />
        )}
      </div>
    </th>
  );
};

export default function FundsTable() {
  const [fundsData, setFundsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFundsData = async () => {
      setLoading(true);
      try {
        const getToken = () => localStorage.getItem('token'); // Assume you're storing the token in localStorage
        const response = await axios.get('https://falcon-algo.onrender.com/api/funds', {
          headers: {
              'Authorization': `Bearer ${getToken()}`
                    }
        });
         
        if (response.data.Success) {
          const fundsData = [response.data.Success];
          setFundsData(fundsData);
        }else {
          console.error('Unexpected response format:', response.data);
        }
      } catch (error) {
        console.error('Error fetching funds data', error);
        setError('Please Enter Correct Session Token');
      } finally {
        setLoading(false);
      }
    };

    

    fetchFundsData();
  }, []);

  const data = useMemo(() => fundsData, [fundsData]);

  const formatAmount = (value) => {
    return parseFloat(value).toFixed(2);
  };

  const columns = useMemo(
    () => [
      { Header: "Bank Account", accessor: "bank_account", width: 120, align: 'right' },
      { Header: "Total Bank Balance", accessor: "total_bank_balance", width: 150, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: "Allocated Equity", accessor: "allocated_equity", width: 140, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: "Allocated FNO", accessor: "allocated_fno", width: 130, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: "Block by Trade Equity", accessor: "block_by_trade_equity", width: 160, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: "Block by Trade FNO", accessor: "block_by_trade_fno", width: 160, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: "Block by Trade Balance", accessor: "block_by_trade_balance", width: 180, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: "Unallocated Balance", accessor: "unallocated_balance", width: 160, align: 'right', Cell: ({ value }) => formatAmount(value) },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
    state,
  } = useTable(
    {
      columns,
      data,
      initialState: { globalFilter: '' },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    useFlexLayout
  );

  return (
    <Container>
      <Title>Funds</Title>
      {error && <ErrorMessage> {error}</ErrorMessage>}
      {loading ? (
        <div tw="flex justify-center items-center">
          <Spinner animation="border" role="status" />
          <span className="sr-only" tw="ml-2 text-gray-700">Loading...</span>
          
        </div>
        
      ) : (
        <>
          <InputGroup className="my-3">
            <FilterInput
              value={state.globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search..."
            />
          </InputGroup>
          <TableWrapper>
            <StyledTable {...getTableProps()}>
              <thead>
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                      <ResizableHeader
                        key={column.id}
                        column={column}
                        className="text-center"
                      />
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map(row => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map(cell => (
                        <td 
                          {...cell.getCellProps()} 
                          className={cell.column.align === 'right' ? 'align-right' : 'align-left'}
                        >
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </StyledTable>
          </TableWrapper>
        </>
      )}
    </Container>
  );
}
